import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Presentation shared by every activity feed: the vertical rail with its
 * tone-coloured icon bubble, and the relative timestamp.
 *
 * Deliberately knows nothing about any one feed's entry shape — each timeline
 * (collection threads, cash receipts) keeps its own kind→icon/label mapping
 * and feeds the result in here, so they look like one component without any
 * of them having to share a DTO.
 */

export type ActivityTone = 'stone' | 'note' | 'amber' | 'purple' | 'green';

const TONE_STYLES: Record<ActivityTone, { bg: string; fg: string }> = {
  stone: { bg: 'bg-stone-100', fg: 'text-stone-700' },
  note: { bg: 'bg-amber-100', fg: 'text-amber-700' },
  amber: { bg: 'bg-amber-100', fg: 'text-amber-700' },
  purple: { bg: 'bg-purple-100', fg: 'text-purple-700' },
  green: { bg: 'bg-green-100', fg: 'text-green-700' },
};

interface ActivityRailProps {
  icon: React.ReactNode;
  tone: ActivityTone;
  /** Suppresses the connecting line below the last entry. */
  isLast: boolean;
  children: React.ReactNode;
}

export function ActivityRail({
  icon,
  tone,
  isLast,
  children,
}: ActivityRailProps) {
  const styles = TONE_STYLES[tone];
  return (
    <li className="grid grid-cols-[28px_1fr] gap-3 relative pb-3">
      <div className="flex flex-col items-center relative">
        <div
          className={`h-6 w-6 rounded-full ${styles.bg} ${styles.fg} flex items-center justify-center border-2 border-background z-10`}
        >
          {icon}
        </div>
        {!isLast && (
          <div className="absolute top-5 bottom-0 left-1/2 -translate-x-1/2 w-px bg-border" />
        )}
      </div>
      <div className="min-w-0 pt-0.5">{children}</div>
    </li>
  );
}

/**
 * Relative time with the absolute timestamp on hover. Falls back to the raw
 * string rather than throwing — `format` throws on an Invalid Date, which
 * would take down a whole feed over one malformed value.
 */
export function ActivityWhen({ at }: { at: string }) {
  const parsed = parseISO(at);
  const valid = !Number.isNaN(parsed.getTime());
  const relative = valid
    ? formatDistanceToNow(parsed, { addSuffix: true })
    : at;
  const absolute = valid ? format(parsed, "MMM d, yyyy 'at' h:mm a") : at;
  return (
    <span className="text-xs text-muted-foreground" title={absolute}>
      {relative}
    </span>
  );
}
